import React, { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  GridComponent,
  Page,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Filter,
  Sort,
} from "@syncfusion/ej2-react-grids";

import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import { equipmentModelsService } from "../../Services/equipment.models.service";
import { manufacturerService } from "../../Services/manufacturer.service";

export default function EquipmentCommandAccessLevels() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [toastParams, setToastParams] = useState("");
  const [loadingAccessLevelId, setLoadingAccessLevelId] = useState(null);

  const [manufacturers, setManufacturers] = useState([]);
  const [selectedManufacturerId, setSelectedManufacturerId] = useState("");
  const [selectedCommandId, setSelectedCommandId] = useState("");
  const [accessLevels, setAccessLevels] = useState([]);
  const [pageSize, setPageSize] = useState(10);

  const calculatePageSize = () => {
    const available = window.innerHeight - 220;
    setPageSize(Math.max(5, Math.floor(available / 30)));
  };

  useLayoutEffect(() => {
    calculatePageSize();
  }, []);

  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(calculatePageSize, 300);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    dispatch({ type: "title", value: t("EQUIPMENT_COMMAND_ACCESS_LEVELS.TITLE") });
    loadManufacturers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const showErrorToast = (error) => {
    const msg = error?.response?.data?.message || "Ocorreu um erro ao processar a requisição.";
    setToastParams(JSON.stringify({ show: true, title: "Erro", body: msg, color: "danger" }));
  };

  const loadManufacturers = async () => {
    try {
      const response = await manufacturerService.getById(0);
      setManufacturers(Array.isArray(response) ? response : []);
    } catch {
      setManufacturers([]);
    }
  };

  // No intermediate models/commands step: manufacturer selection will be used as the `commandId`.

  const loadAccessLevels = async (commandId) => {
    if (!commandId) {
      setAccessLevels([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await equipmentModelsService.fetchEquipmentCommandAccessLevels(commandId);
      setAccessLevels(Array.isArray(response) ? response : response ? [response] : []);
    } catch (error) {
      showErrorToast(error);
      setAccessLevels([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManufacturerChange = (e) => {
    const manufacturerId = e.target.value;
    const commandId = manufacturerId === "" ? 0 : manufacturerId;
    setSelectedManufacturerId(manufacturerId);
    // use commandId = 0 when 'Todos os fabricantes' selected
    setSelectedCommandId(commandId);
    setAccessLevels([]);
    loadAccessLevels(commandId);
  };

  const handleCheckboxChange = async (item) => {
    if (!selectedCommandId) return;

    const accessLevelId = item.accessLevelId ?? item.AccessLevelId;
    const linked = (item.linked ?? item.Linked) === "S";
    setLoadingAccessLevelId(accessLevelId);

    try {
      const response = linked
        ? await equipmentModelsService.unlinkEquipmentCommandAccessLevel(selectedCommandId, accessLevelId)
        : await equipmentModelsService.linkEquipmentCommandAccessLevel(selectedCommandId, accessLevelId);

      if (response !== null) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: "Sucesso",
            body: linked ? "Nível de acesso desvinculado com sucesso." : "Nível de acesso vinculado com sucesso.",
            color: "success",
          })
        );
        await loadAccessLevels(selectedCommandId);
      } else {
        showErrorToast({});
      }
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoadingAccessLevelId(null);
    }
  };

  const checkboxTemplate = (item) => {
    const accessLevelId = item.accessLevelId ?? item.AccessLevelId;
    const linked = (item.linked ?? item.Linked) === "S";

    return (
      <input
        type="checkbox"
        checked={linked}
        disabled={loadingAccessLevelId === accessLevelId}
        onChange={() => handleCheckboxChange(item)}
      />
    );
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <div className="card p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">{t("EQUIPMENT_COMMAND_ACCESS_LEVELS.CARD_TITLE")}</h5>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <label className="form-label">Fabricante</label>
            <select
              className="form-select"
              value={selectedManufacturerId}
              onChange={handleManufacturerChange}
            >
              <option value="">Todos os fabricantes</option>
              {manufacturers.map((item) => {
                const id = item.manufacturerId ?? item.ManufacturerId;
                const name = item.manufacturerName ?? item.ManufacturerName ?? id;

                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </div>
          {/* Only manufacturer filter is shown; manufacturerId acts as commandId */}
        </div>

        {isLoading && <Spinner />}

        <GridComponent
          dataSource={accessLevels}
          allowPaging
          allowFiltering
          allowSorting
          filterSettings={{ type: "Menu" }}
          pageSettings={{ pageSize }}
          rowHeight={30}
          height="auto"
          locale="pt-BR"
        >
          <ColumnsDirective>
            <ColumnDirective
              headerText=""
              width="10"
              textAlign="Center"
              template={checkboxTemplate}
              allowFiltering={false}
              allowSorting={false}
            />
            <ColumnDirective
              field="accessLevelDescription"
              headerText="Descrição"
              width="320"
              textAlign="Left"
            />
          </ColumnsDirective>
          <Inject services={[Page, Filter, Sort]} />
        </GridComponent>
      </div>
    </div>
  );
}
